import React, { FC } from "react";
import { observer } from "mobx-react-lite";
import { AutoSizer } from "react-virtualized-auto-sizer";
import { List, RowComponentProps, useDynamicRowHeight } from "react-window";

import DatasetStorage from "@core/workspace/dataset-storage/DatasetStorage";
import ProfilerTable from "@core/profiler/ProfilerTable";
import { Phrase } from "@core/profiler/types";
import Cluster from "@core/profiler/Cluster";
import GridLayout from "@core/misc/GridLayout";

import Row from "../Row";
import * as S from "./styled";

interface Props {
  profiler: ProfilerTable;
  dataset: DatasetStorage | undefined;
  layout: GridLayout;
}

const Table: FC<Props> = ({ profiler, dataset, layout }) => {
  const rowHeights = useDynamicRowHeight({ defaultRowHeight: 32 });

  const handleExpand = (row: Cluster, expanded: boolean) => {
    row.toggleExpand(expanded);
  };

  const handleShowDialog = (phrase: Phrase) => {
    layout.expandPanel("runner");
    void profiler.openSession(phrase);
  };

  const setRowHeight = (index: number, size: number) => rowHeights.setRowHeight(index, size);

  const TableRow = ({ index, style }: RowComponentProps) => {
    const cluster = profiler.filter?.filteredData[index];
    if (!cluster) return null;

    return (
      <Row
        style={style}
        index={index}
        searchRequest={profiler.filter?.searchRequest || ""}
        dataset={dataset}
        key={cluster.id}
        cluster={cluster}
        currentSessionPhrase={profiler.currentSessionPhrase}
        setRowHeight={setRowHeight}
        onExpand={handleExpand}
        onShowDialog={handleShowDialog}
      />
    );
  };

  return (
    <S.Table $isRunnerOpen={layout.isExpanded("runner")} className="data-markup-table">
      <AutoSizer
        style={{ width: "100%", height: "100%" }}
        renderProp={({ width, height }) => (
          <List
            style={{ width: width ?? "100%", height: height ?? 0 }}
            rowCount={profiler.filter?.filteredData.length ?? 0}
            rowHeight={rowHeights}
            overscanCount={20}
            rowProps={{}}
            rowComponent={TableRow}
          />
        )}
      />
    </S.Table>
  );
};

export default observer(Table);
